import * as React from 'react';
import { SimpleCall, ISimpleCallProps, EPageTypes, EPlatformTypes } from '@cian/frontend-newbuilding-call-component';
import { useDispatch, useSelector } from 'react-redux';

import { setOpenedPhones } from '../../actions/openedPhones';
import { getNewbuildingId, getBuilders, getSellers } from '../../selectors/newbuilding';
import { getOpenedPhones } from '../../selectors/phones/getOpenedPhones';
import { setUserOpenedPhoneInNewobject } from '../../utils/lastHopePopup';
import { useApplicationContext } from '../../utils/withApplicationContext';

interface IShowPhoneParams {
  realtyUserId: number;
  phone: string;
}

type TSimpleCallProps = Pick<
  ISimpleCallProps,
  'onOpenContactsClick' | 'openContactsStyles' | 'showPhoneStyles' | 'SinglePhoneComponent'
>;

export interface ICallButtonProps extends TSimpleCallProps {
  placeType: string;
  preserveOpen?: boolean;
  singlePhoneButtonText?: string;
  multiplePhoneButtonText?: string;
  onShowPhoneClick?(params: IShowPhoneParams): void;
}

export const CallButton = ({
  placeType,
  onShowPhoneClick,
  onOpenContactsClick,
  openContactsStyles,
  showPhoneStyles,
  SinglePhoneComponent,
  preserveOpen = true,
  singlePhoneButtonText,
  multiplePhoneButtonText,
}: ICallButtonProps) => {
  const { httpApi, logger } = useApplicationContext();
  const newbuildingId = useSelector(getNewbuildingId);
  const opened = useSelector(getOpenedPhones)[placeType];
  const builders = useSelector(getBuilders);
  const sellers = useSelector(getSellers);
  const dispatch = useDispatch();

  const callBuilders = React.useMemo(() => {
    return builders.filter(builder => builder.phone?.number && builder.showPhone);
  }, [builders]);

  const callSellers = React.useMemo(() => {
    return sellers.filter(seller => seller.phone?.number && seller.showPhone);
  }, [sellers]);

  const handleShowPhoneClick = React.useCallback(
    (params: IShowPhoneParams) => {
      const { realtyUserId, phone } = params;
      const newOpened = [{ realtyUserId, phone }, ...(opened || [])];

      if (preserveOpen) {
        dispatch(setOpenedPhones({ [placeType]: newOpened }));
        setUserOpenedPhoneInNewobject(newbuildingId);
      }

      if (onShowPhoneClick) {
        onShowPhoneClick(params);
      }
    },
    [newbuildingId, opened, onShowPhoneClick, placeType, dispatch, preserveOpen],
  );

  const pageType = EPageTypes.NewbuildingCard;
  const platformType = EPlatformTypes.WebDesktop;

  const userProps = React.useMemo(
    () => ({
      builders: callBuilders,
      sellers: callBuilders.length > 0 ? [] : callSellers,
      singlePhoneButtonText,
      multiplePhoneButtonText,
    }),
    [callBuilders, callSellers, multiplePhoneButtonText, singlePhoneButtonText],
  );

  return (
    <SimpleCall
      SinglePhoneComponent={SinglePhoneComponent}
      onOpenContactsClick={onOpenContactsClick}
      onShowPhoneClick={handleShowPhoneClick}
      openContactsStyles={openContactsStyles}
      showPhoneStyles={showPhoneStyles}
      httpApi={httpApi}
      logger={logger}
      pageType={pageType}
      platformType={platformType}
      placeType={placeType}
      {...userProps}
    />
  );
};

CallButton.displayName = 'CallButton';
